import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import Modal from "react-modal";
import { displayToast } from "../utils/Toast";
import {
  createSubcategory,
  updateSubcategory,
  fetchVendorCategoriesList,
} from "../apis/transaction";
import "../styles/components/MasterDataModal.css";

const SubcategoryManagementModal = ({
  isOpen,
  onClose,
  onSuccess,
  mode = "add",
  subcategoryData = null,
  selectedCategoryId = null,
  selectedCategoryName = "",
}) => {
  const [subcategoryName, setSubcategoryName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState("active");
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const isCategoryFixed = Boolean(selectedCategoryId) || mode === "edit";

  const resolvedCategoryName =
    selectedCategoryName ||
    subcategoryData?.category_name ||
    categories.find((cat) => cat.id === parseInt(categoryId))?.category_name ||
    "";

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && mode === "edit" && subcategoryData) {
      setSubcategoryName(subcategoryData.subcategory_name || "");
      setCategoryId(subcategoryData.category_id?.toString() || "");
      setStatus(subcategoryData.status || "active");
    } else if (isOpen && mode === "add") {
      setSubcategoryName("");
      setCategoryId(selectedCategoryId?.toString() || "");
      setStatus("active");
    }
  }, [isOpen, mode, subcategoryData, selectedCategoryId]);

  const loadCategories = async () => {
    setCategoryLoading(true);
    try {
      const response = await fetchVendorCategoriesList();
      const items = response?.items || response?.data || (Array.isArray(response) ? response : []);
      setCategories(items);
    } catch (error) {
      displayToast("Failed to fetch categories", "error");
    } finally {
      setCategoryLoading(false);
    }
  };

  const resetForm = () => {
    setSubcategoryName("");
    setCategoryId(selectedCategoryId?.toString() || "");
    setStatus("active");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!subcategoryName.trim()) {
      displayToast("Please enter a subcategory name", "error");
      return;
    }

    if (!categoryId) {
      displayToast("Please select a category", "error");
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        subcategory_name: subcategoryName.trim(),
        category_id: parseInt(categoryId),
        status: status,
      };

      let response;
      if (mode === "add") {
        response = await createSubcategory(payload);
        if (response && (response.id || response.status === "success")) {
          displayToast("Subcategory created successfully", "success");
          resetForm();
          onSuccess(parseInt(categoryId));
        } else if (response?.message) {
          displayToast(response.message, "error");
        } else {
          displayToast("Failed to create subcategory", "error");
        }
      } else if (mode === "edit" && subcategoryData?.id) {
        response = await updateSubcategory(subcategoryData.id, payload);
        if (response && (response.status === "success" || response.id)) {
          displayToast("Subcategory updated successfully", "success");
          resetForm();
          onSuccess(parseInt(categoryId));
        } else if (response?.message) {
          displayToast(response.message, "error");
        } else {
          displayToast("Failed to update subcategory", "error");
        }
      }
    } catch (error) {
      displayToast("Error saving subcategory: " + (error.message || "Unknown error"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="master-data-modal-container subcategory-management-modal"
      overlayClassName="modal-overlay compact-modal-overlay"
      contentLabel={mode === "add" ? "Add Subcategory" : "Edit Subcategory"}
    >
      <div className="master-data-modal-header" style={{ backgroundColor: "#8B9BBD" }}>
        <div className="master-data-modal-title-container">
          <h2 className="master-data-modal-title">
            {mode === "add" ? "Add Subcategory" : "Edit Subcategory"}
          </h2>
          <X
            className="sales-form-icons cursor-pointer"
            onClick={handleClose}
            size={24}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="master-data-modal-form">
        <div className="master-data-modal-form-fields">
          <div className="master-data-modal-field">
            <label>Category *</label>
            {isCategoryFixed ? (
              <input
                type="text"
                className="master-data-modal-input non-editable"
                value={resolvedCategoryName || "Selected category"}
                readOnly
              />
            ) : categoryLoading ? (
              <div className="vm-category-loading">Loading categories...</div>
            ) : (
              <select
                className="master-data-modal-input"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                required
                disabled={mode === "edit"}
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.category_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="master-data-modal-field">
            <label>Subcategory Name *</label>
            <input
              type="text"
              className="master-data-modal-input"
              placeholder="Enter subcategory name"
              value={subcategoryName}
              onChange={(e) => setSubcategoryName(e.target.value)}
              required
              maxLength={100}
              autoFocus
            />
          </div>

          <div className="master-data-modal-field">
            <label>Status</label>
            <select
              className="master-data-modal-input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="master-data-modal-footer">
          {isLoading ? (
            <div className="loader-container">
              <span className="loader"></span>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                className="master-data-modal-cancel-button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="master-data-modal-save-button"
                disabled={isLoading}
              >
                {mode === "add" ? "Create" : "Update"}
              </button>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default SubcategoryManagementModal;
